import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { MenuBarItem, MenuItem } from "./types";

function FileMenuItem({ item }: { item: MenuItem }) {
	if (item.separator) {
		return <DropdownMenuSeparator />;
	}
	if (item.children && item.children.length > 0) {
		return (
			<DropdownMenuSub>
				<DropdownMenuSubTrigger className="text-xs h-7">
					{item.icon}
					<span>{item.label}</span>
				</DropdownMenuSubTrigger>
				<DropdownMenuSubContent>
					{item.children.map((child) => (
						<FileMenuItem key={child.id} item={child} />
					))}
				</DropdownMenuSubContent>
			</DropdownMenuSub>
		);
	}
	return (
		<DropdownMenuItem
			className="text-xs h-7"
			disabled={item.disabled}
			onClick={item.onClick}
			title={item.tooltip}
		>
			{item.icon}
			<span>{item.label}</span>
			{item.shortcut && (
				<DropdownMenuShortcut>{item.shortcut}</DropdownMenuShortcut>
			)}
		</DropdownMenuItem>
	);
}

/**
 * File-style menu bar (Файл / Правка / Вид / …) rendered inline in the global
 * TopBar. Modules push items via `useWorkspaceContext().setMenuBar` (typically
 * through `<Workspace config={{ menuBar }} />`).
 */
export function FileMenuBar({ items }: { items: MenuBarItem[] }) {
	if (items.length === 0) return null;
	return (
		<nav className="flex items-center shrink-0">
			<div className="w-px h-4 bg-border mx-1.5" />
			{items.map((menu) => (
				<DropdownMenu key={menu.id}>
					<DropdownMenuTrigger asChild>
						<Button
							variant="ghost"
							size="sm"
							className={cn(
								"h-7 px-2 text-xs",
								"text-muted-foreground hover:text-foreground",
								"data-[state=open]:bg-accent data-[state=open]:text-foreground",
								"cursor-default",
							)}
						>
							{menu.label}
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align="start"
						sideOffset={4}
						className="min-w-[200px]"
					>
						{menu.items.map((item) => (
							<FileMenuItem key={item.id} item={item} />
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			))}
		</nav>
	);
}
